import { Badge, Dropdown, Form, Modal } from "react-bootstrap";
import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ANNOUNCEMENT_STATUS,
  ANNOUNCEMENT_TYPE,
} from "../../../services/flitEnum";
import { FlitServiceContext } from "../../../services/flitService";
import { IoCloseOutline } from "react-icons/io5";

import { TailSpin } from "react-loader-spinner";
import { getAnnouncementTypeCssClasses, getAnnouncementStatusCssClasses } from "./announcementHelper";
import cf from '../../../services/codeformatter'


export const AnnouncementDetailModal = ({
  row,
  open,
  setOpen,
  onClose,
  fetchAnnouncements,
  isAdminView,
}) => {
  const MAX_TITLE_LENGTH = 1024;
  const { flitSvc } = useContext(FlitServiceContext);
  const { user } = useSelector(state => state.auth);
  const [isEditMode, setIsEditMode] = useState(false);
  const intl = useIntl();
  // can use codeformatter?
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .min(1, "Title must be at least 1 character")
      .max(
        MAX_TITLE_LENGTH,
        `Title must be at most ${MAX_TITLE_LENGTH} characters`
      ),
    message: Yup.string(),
    type: Yup.mixed()
      .oneOf(Object.values(ANNOUNCEMENT_TYPE), "Invalid announcement type")
      .required("Type is required"),
    status: Yup.string().oneOf(
      Object.values(ANNOUNCEMENT_STATUS),
      "Invalid announcement status"
    ),
  });

  const formik = useFormik({
    initialValues: {
      title: row.title ?? "",
      message: row.message ?? "",
      type: row.type ?? ANNOUNCEMENT_TYPE.GENERAL,
      status: row.status,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!row) return;
      setSubmitting(true);
      try {
        const req = {
          id: row.id,
          title: values.title,
          message: values.message,
          type: values.type,
          status: values.status,
          submittedById: row.submittedById,
          createdBy: row.createdBy,
          createdOn: row.createdOn,
          lastUpdatedBy: user.id,
          lastUpdatedOn: row.lastUpdatedOn,
        };

        const res = await flitSvc.updateAnnouncement(req);

        if (res.status === 200 && res.data) {
          setOpen(false);
          fetchAnnouncements();
        }
      } catch (error) {
        console.error("Error updating announcement", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const initializeValues = () => {
    if (row && open) {
      formik.resetForm({
        title: row.title ?? "",
        message: row.message ?? "",
        type: row.type ?? ANNOUNCEMENT_TYPE.GENERAL,
        status: row.status ?? true,
      });
    }
  };

  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      formik.resetForm();
    }
  }, [formik, open]);

  return (
    row && (
      <Modal show={open} onHide={onClose} centered>
        <Modal.Header>
          <div className="d-flex g-2">
            <Modal.Title>
              {intl.formatMessage({ id: "ANNOUNCEMENT" })}
            </Modal.Title>

            {!isEditMode && (
              <>
                <div className="ml-3 mt-1">
                  <span className={getAnnouncementTypeCssClasses(formik.values.type)}>
                  {cf.format('AnnouncementType', formik.values.type, intl.locale)}
                </span>
                </div>
                {isAdminView && (
                  <div className="ml-3 h-100">
                    <span className={getAnnouncementStatusCssClasses(formik.values.status)}>
                      {cf.format('AnnouncementStatus', formik.values.status, intl.locale)}
                    </span>
                    
                  </div>
                )}
              </>
            )}
          </div>
          <button
            className="close"
            style={{ border: "transparent" }}
            onClick={() => setOpen(false)}
          >
            <IoCloseOutline />
          </button>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <Form onSubmit={formik.handleSubmit}>
            <div className="mb-3 d-flex">
              <div className="mr-2">
                {isEditMode && (
                  <div className="g-3 d-flex align-items-center">
                    <Dropdown>
                      <Dropdown.Toggle variant="success" id="dropdown-basic">
                        {cf.format('AnnouncementType', formik.values.type, intl.locale)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Object.keys(ANNOUNCEMENT_TYPE).map(at => (
                          <Dropdown.Item
                            key={at}
                            className="w-44"
                            style={{ minWidth: "200px", maxWidth: "100%" }}
                            onClick={() =>
                              formik.setFieldValue(
                                "type",
                                ANNOUNCEMENT_TYPE[at]
                              )
                            }
                          >
                            {cf.format('AnnouncementType', ANNOUNCEMENT_TYPE[at], intl.locale)}

                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    {formik.touched.type && formik.errors.type && (
                      <div className="text-danger small">
                        {formik.errors.type}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <di className="mr-2">
                {isEditMode && (
                  <div className="g-3 d-flex align-items-center">
                    <Dropdown>
                      <Dropdown.Toggle variant="default" id="dropdown-basic">
                        {cf.format('AnnouncementStatus', formik.values.status, intl.locale)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Object.keys(ANNOUNCEMENT_STATUS).map(at => (
                          <Dropdown.Item
                            key={at}
                            className="w-44"
                            style={{ minWidth: "200px", maxWidth: "100%" }}
                            onClick={() =>
                              formik.setFieldValue(
                                "status",
                                ANNOUNCEMENT_STATUS[at]
                              )
                            }
                          >
                            {cf.format('AnnouncementStatus', ANNOUNCEMENT_STATUS[at], intl.locale)}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    {formik.touched.status && formik.errors.type && (
                      <div className="text-danger small">
                        {formik.errors.type}
                      </div>
                    )}
                  </div>
                )}
              </di>
            </div>
            <div className="mb-3">
              {isEditMode ? (
                <div className="d-flex flex-column gap-3">
                  <Form.Label>{intl.formatMessage({ id: "TITLE" })}</Form.Label>
                  <Form.Control
                    type="text"
                    style={{ width: "100%", marginBottom: "4px" }}
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    // maxLength={MAX_TITLE_LENGTH}
                    isInvalid={formik.touched.title && !!formik.errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.title}
                  </Form.Control.Feedback>
                </div>
              ) : (
                <div>
                  <span
                    className="fw-bold"
                    style={{ fontWeight: 600, fontSize: "1.25rem" }}
                  >
                    {formik.values.title}
                  </span>
                </div>
              )}
            </div>
            <div>
              {isEditMode ? (
                <div>
                  <Form.Label>{intl.formatMessage({ id: "MESSAGE" })}</Form.Label>
                  <Form.Control
                    style={{
                      width: "100%",
                      minHeight: "12rem",
                      marginBottom: "4px",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      wordBreak: "break-all",
                      whiteSpace: "pre-wrap",
                    }}
                    name="message"
                    value={formik.values.message}
                    as="textarea"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      formik.touched.message && !!formik.errors.message
                    }
                  />
                  <Form.Control.Feedback className="word-wrap" type="invalid">
                    {formik.errors.message}
                  </Form.Control.Feedback>
                </div>
              ) : (
                <span className="word-wrap">{formik.values.message}</span>
              )}
            </div>
          </Form>
        </Modal.Body>
        {isAdminView && (
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100 align-items-center">
              <button
                className="btn btn-light btn-elevate"
                onClick={() => {
                  setIsEditMode(!isEditMode);
                  if (isEditMode) {
                    initializeValues(); // reset values to og
                  }
                }}
              >
                {isEditMode ? intl.formatMessage({ id: "CANCEL" }): intl.formatMessage({ id: "EDIT" })}
              </button>
              {isEditMode && (
                <button
                  type="button"
                  className="btn btn-primary btn-elevate w-24"
                  disabled={
                    !formik.isValid ||
                    formik.isSubmitting ||
                    formik.values.title.length === 0 ||
                    !formik.dirty
                  }
                  onClick={formik.handleSubmit}
                >
                  {formik.isSubmitting ? (
                    <TailSpin height={12} width={12} color="black" />
                  ) : (
                    intl.formatMessage({ id: "SUBMIT_BUTTON" })
                  )}
                </button>
              )}
            </div>
          </Modal.Footer>
        )}
      </Modal>
    )
  );
};
